import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Quote as QuoteIcon } from "lucide-react";
import { Reveal, PageHeader, SectionLabel } from "@/components/Reveal";

const CREDENTIALS = [
  "IBBI Registered Valuer — Land & Building (Reg. No. IBBI/RV/02/2026/16277)",
  "Associate Member of IOV (Membership No. A-31252)",
  "B.Tech in Civil Engineering",
  "12+ years of experience in valuation & advisory services",
];

const PILLARS = [
  { title: "Our Purpose", desc: "To create value through accurate and independent valuation insights that empower better business decisions." },
  { title: "Our Commitment", desc: "Upholding the highest standards of integrity, independence and professional excellence in every engagement." },
  { title: "Our Approach", desc: "Research-driven, data-backed and client-focused approach delivering credible and defensible outcomes." },
  { title: "Our Vision", desc: "To be a trusted valuation partner recognized for quality, reliability and professionalism across India and beyond." },
];

const Profile = () => (
  <section className="py-24 px-6 sm:px-12" data-testid="founder-profile">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <div className="lg:col-span-4">
        <Reveal>
          <div
            className="rounded-[28px] border-[6px] border-ink border-l-gold overflow-hidden bg-white"
            data-testid="founder-photo-wrapper"
          >
              <div className="aspect-[4/5] overflow-hidden bg-slate-100">
                <img
                  src="/images/founder.jpg"
                  alt="Manish Kumar Sharma — Founder & Director, Auric Valuation"
                  data-testid="founder-photo"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <p className="font-serif text-2xl text-ink" data-testid="founder-name">Er. Manish Kumar Sharma</p>
                <p className="mt-1 text-xs tracking-[0.25em] uppercase text-gold font-semibold">Founder & Director</p>
              </div>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-8">
        <Reveal>
          <SectionLabel>Credentials</SectionLabel>
          <ul className="mt-8 space-y-5">
            {CREDENTIALS.map((c, i) => (
              <li key={i} className="flex gap-4 items-start" data-testid={`credential-${i}`}>
                <BadgeCheck size={18} className="text-gold shrink-0 mt-1" />
                <p className="text-base leading-relaxed text-slate-700">{c}</p>
              </li>
            ))}
          </ul>
          <div className="mt-12 border-t border-border pt-10">
            <SectionLabel>Experience</SectionLabel>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Extensive experience in valuation of real estate, plant & machinery, financial assets,
              infrastructure and other intangible assets across diverse sectors. Before founding
              Auric Valuation, he was associated with various financial institutions and corporates,
              gaining extensive exposure in valuation, credit, risk assessment and advisory across India.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Quote = () => (
  <section className="bg-ink text-cream py-24 sm:py-32 px-6 sm:px-12" data-testid="founder-quote">
    <div className="max-w-4xl mx-auto">
      <Reveal>
        <QuoteIcon size={48} className="text-gold fill-gold rotate-180" strokeWidth={0} />
        <p className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl leading-snug tracking-tight">
          With a strong foundation of integrity, expertise and innovation, we are committed to
          delivering accurate, reliable and insightful valuation services that create value, build
          trust and empower confident decision-making.
        </p>
        <div className="flex justify-end mt-4">
          <QuoteIcon size={48} className="text-gold fill-gold" strokeWidth={0} />
        </div>
        <p className="mt-2 text-xs tracking-[0.3em] uppercase font-semibold text-gold-light">
          Manish Kumar Sharma — Founder & Director
        </p>
      </Reveal>
    </div>
  </section>
);

const Pillars = () => (
  <section className="py-24 px-6 sm:px-12 bg-white border-b border-border" data-testid="founder-pillars">
    <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
      {PILLARS.map((p, i) => (
        <Reveal key={p.title} delay={(i % 2) * 0.1} className="bg-white">
          <div className="p-10 hover:-translate-y-1 transition-transform duration-300 h-full" data-testid={`pillar-card-${i}`}>
            <p className="font-serif text-lg text-gold/60">{String(i + 1).padStart(2, "0")}</p>
            <h3 className="mt-4 font-serif text-2xl text-ink">{p.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-500">{p.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <div className="max-w-7xl mx-auto mt-16">
      <Reveal>
        <Link
          to="/contact"
          data-testid="founder-contact-button"
          className="inline-flex items-center gap-2 bg-ink text-white text-xs tracking-[0.25em] uppercase font-bold px-8 py-4 hover:bg-gold transition-colors duration-300"
        >
          Work With Us <ArrowRight size={14} />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Founder() {
  return (
    <>
      <PageHeader
        label="Founder & Director"
        lines={["Leadership rooted in", "expertise & ethics."]}
        sub="Auric Valuation is led by Er. Manish Kumar Sharma — an IBBI Registered Valuer with over 12 years of valuation and advisory experience."
      />
      <Profile />
      <Quote />
      <Pillars />
    </>
  );
}
